/**
 * Mağaza state (Satışlar, İlanlar): the GET /shop read model, loaded when one of the two
 * screens opens and read again on Tekrar dene. Read only; nothing here decides anything.
 */

import { useSyncExternalStore } from 'react';
import type { TwinApi } from '../api/client.js';
import type { ShopView } from '../api/shop.js';
import { useStore } from './context.js';

export interface ShopState {
  view: ShopView | null;
  error: string | null;
  loading: boolean;
}

export class ShopStore {
  private state: ShopState = { view: null, error: null, loading: false };
  private readonly listeners = new Set<() => void>();
  private inFlight: Promise<void> | null = null;

  constructor(private readonly api: TwinApi) {}

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly getState = (): ShopState => this.state;

  private set(patch: Partial<ShopState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener();
  }

  /** Loads once; a screen calls it on mount. */
  ensureLoaded(): Promise<void> {
    if (this.state.view !== null || this.inFlight !== null) return this.inFlight ?? Promise.resolve();
    return this.reload();
  }

  /** Kontrol listesi B1: "Şimdi senkronize et". The worker reads Etsy within five minutes. */
  async requestSync(): Promise<boolean> {
    const answer = await this.api.requestShopSync();
    return answer.queued;
  }

  reload(): Promise<void> {
    this.set({ loading: true });
    const run = (async () => {
      try {
        const view = await this.api.getShop();
        this.set({ view, error: null, loading: false });
      } catch (error) {
        this.set({ error: error instanceof Error ? error.message : String(error), loading: false });
      }
    })();
    this.inFlight = run;
    void run.finally(() => {
      if (this.inFlight === run) this.inFlight = null;
    });
    return run;
  }
}

export function useShop(): { store: ShopStore; state: ShopState } {
  const store = useStore().shop;
  const state = useSyncExternalStore(store.subscribe, store.getState, store.getState);
  return { store, state };
}
